import BindingContext from '@/BindingContext';
import Utilities from '@/Utility/Utilities';

/**
 * Kapselt die komplette Tastaturnavigation der Toolbar nach dem WAI-ARIA APG Toolbar Pattern
 * (Roving Tabindex) – inklusive Registrierung und Deregistrierung der keydown-Handler.
 */
export default class ToolbarKeyboardController extends BindingContext {

    private static readonly NAVIGATION_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    private static readonly VERTICAL_KEYS = ['ArrowUp', 'ArrowDown', 'Home', 'End'];

    private static readonly ITEM_SELECTOR = '[tabindex]';

    public constructor() {
        super();
        this.isDisposable = true;
    }

    /**
     * Registriert die horizontale Pfeilnavigation auf der Toolbar.
     * @param toolbar Das Toolbar-Element
     */
    public setupToolbarNavigation(toolbar: JQuery): void {
        this.addEventHandler('keydown', toolbar, (event: JQuery.KeyDownEvent) => {
            if (this.navigate(this.getFocusableItems(toolbar), event.key)) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            // Aktivierung (Space/Enter) kann den Fokus aus der Toolbar nehmen – danach zurückholen.
            if (Utilities.isConfirmKey(event.key))
                this.async(() => this.restoreFocus(toolbar), 0);
        });
    }

    /**
     * Holt den Fokus in die Toolbar zurück, falls er sie verlassen hat. Setzt den Roving-Tabindex neu
     * und fokussiert das erste erreichbare Item.
     * @param toolbar Das Toolbar-Element
     */
    private restoreFocus(toolbar: JQuery): void {
        if (toolbar[0].contains(document.activeElement)) return;

        const items = this.getFocusableItems(toolbar);
        if (!items.length) return;

        // Auch veraltete tabindex=0 (z. B. auf einem inzwischen deaktivierten Item) entfernen.
        toolbar.find(ToolbarKeyboardController.ITEM_SELECTOR).attr('tabindex', '-1');
        items.first().attr('tabindex', '0');
        this.focusItem(items, 0);
    }

    /**
     * Setzt tabindex=0 auf das erste erreichbare Item, falls noch kein Tab-Einstieg existiert.
     * Da alle Toolbar-Links auf tabindex=-1 starten, wäre die Toolbar sonst per Tab nicht erreichbar.
     * @param toolbar Das Toolbar-Element
     */
    public ensureSingleTabStop(toolbar: JQuery): void {
        if (toolbar.find('[tabindex="0"]').length > 0) return;
        toolbar.find(ToolbarKeyboardController.ITEM_SELECTOR)
            .filter((_, el) => this.isReachable(el))
            .first()
            .attr('tabindex', '0');
    }

    /**
     * Registriert die Tastaturbedienung des Overflow-Menüs: Aktivierung des <div>-Öffner-Buttons
     * sowie Escape/Bestätigung/vertikale Navigation innerhalb des Popups.
     * @param menuButton Der Öffner-Button
     * @param overlay Der Popup-Container mit den ausgelagerten Items
     * @param closeMenu Callback zum Schließen des Menüs
     */
    public setupOverflowNavigation(menuButton: JQuery, overlay: JQuery, closeMenu: () => void): void {
        // Der Overflow-Button ist ein <div> – Tastaturaktivierung für Accessibility nachrüsten.
        this.addEventHandler('keydown', menuButton, (event: JQuery.KeyDownEvent) => {
            if (!Utilities.isConfirmKey(event.key)) return;
            event.preventDefault();
            menuButton.trigger('click');
        });

        this.addEventHandler('keydown', overlay, (event: JQuery.KeyDownEvent) => {
            const key = event.key;
            if (key === 'Escape') {
                event.preventDefault();
                closeMenu();
                return;
            }
            if (Utilities.isConfirmKey(key)) {
                // Menü nach der Aktion explizit schließen.
                closeMenu();
                return;
            }
            if (this.navigate(this.getFocusableItems(overlay), key, 'vertical')) {
                event.preventDefault();
                event.stopPropagation();
            }
        });
    }

    /**
     * Setzt den Fokus auf das Item am angegebenen Index.
     * @param items Die Item-Liste
     * @param index Der Index des zu fokussierenden Items
     */
    public focusItem(items: JQuery, index: number): void {
        items.get(index)?.focus({ preventScroll: true });
    }

    /**
     * Macht die geklonten Items des geöffneten Overflow-Menüs wieder per Tastatur erreichbar,
     * initialisiert den Roving-Tabindex und fokussiert das erste Item.
     * @param overlay Der Popup-Container mit den geklonten Items
     */
    public activateOverflowItems(overlay: JQuery): void {
        const overlayButtons = overlay.find(ToolbarKeyboardController.ITEM_SELECTOR)
            .removeClass('toolbar-overflowed')
            .removeAttr('aria-hidden');
        this.focusItem(this.initRoving(overlayButtons), 0);
    }

    /**
     * Erreichbare Items bekommen tabindex=0, der Rest -1; bevorzugt das fokussierte Item.
     * Ausgeblendete Items werden zusätzlich per aria-hidden aus dem Accessibility-Baum genommen.
     * Wandert das fokussierte Item dabei ins Overflow-Menü, wird der Fokus auf den Fallback zurückgesetzt.
     * @param toolbar Das Toolbar-Element
     * @param focusFallback Element, das bei Fokusverlust fokussiert wird (z. B. der Öffner-Button)
     */
    public syncTabReachability(toolbar: JQuery, focusFallback: JQuery): void {
        const reachableItems: HTMLElement[] = [];
        const active = document.activeElement as HTMLElement;

        toolbar.find(ToolbarKeyboardController.ITEM_SELECTOR).each((_, el) => {
            if (this.isReachable(el)) reachableItems.push(el);
            el.setAttribute('tabindex', '-1');

            // Kein aria-hidden auf dem fokussierten Element setzen (Accessibility-Fehler).
            if (this.isHiddenInSecondary(el) && el !== active)
                el.setAttribute('aria-hidden', 'true');
            else
                el.removeAttribute('aria-hidden');
        });

        const focusedReachable = reachableItems.find(el => el === active);
        (focusedReachable ?? reachableItems[0])?.setAttribute('tabindex', '0');

        const focusMovedToOverflow = toolbar[0].contains(active) && this.isHiddenInSecondary(active);
        if (focusMovedToOverflow) this.focusItem(focusFallback, 0);
    }

    /**
     * Roving für einen frisch geöffneten Container: erstes sichtbares Item bekommt tabindex=0, Rest -1.
     * @param items Die Item-Liste
     * @returns Die sichtbaren Items
     */
    private initRoving(items: JQuery): JQuery {
        const visibleItems = items.filter(':visible');
        visibleItems.attr('tabindex', '-1');
        visibleItems.first().attr('tabindex', '0');
        return visibleItems;
    }

    /**
     * Liefert die per Tastatur erreichbaren Items eines Containers.
     * @param container Das Container-Element
     * @returns Die erreichbaren Items
     */
    private getFocusableItems(container: JQuery): JQuery {
        return container.find(ToolbarKeyboardController.ITEM_SELECTOR)
            .filter((_, el) => this.isReachable(el));
    }

    /**
     * Bewegt Fokus und tabindex=0 gemeinsam zum nächsten Item.
     * @param items Die navigierbaren Items
     * @param key Die gedrückte Taste
     * @param orientation Navigationsrichtung
     * @returns true, wenn navigiert wurde
     */
    private navigate(items: JQuery, key: string, orientation: 'horizontal' | 'vertical' = 'horizontal'): boolean {
        const keys = orientation === 'vertical' ? ToolbarKeyboardController.VERTICAL_KEYS : ToolbarKeyboardController.NAVIGATION_KEYS;
        if (!keys.includes(key) || !items.length) return false;

        const currentIdx = items.index(document.activeElement as HTMLElement);
        if (currentIdx < 0) return false;

        const targetIdx = this.computeTargetIndex(key, currentIdx, items.length);
        if (targetIdx === null) return false;

        items.get(currentIdx)?.setAttribute('tabindex', '-1');
        const targetEl = items.get(targetIdx);
        targetEl?.setAttribute('tabindex', '0');
        targetEl?.focus({ preventScroll: true });
        return true;
    }

    /**
     * Berechnet den Zielindex anhand der gedrückten Taste.
     * @param key Die gedrückte Taste
     * @param currentIdx Der aktuelle Index
     * @param count Die Anzahl der Items
     * @returns Der Zielindex oder null, wenn die Taste nicht relevant ist
     */
    private computeTargetIndex(key: string, currentIdx: number, count: number): number | null {
        switch (key) {
            case 'ArrowRight':
            case 'ArrowDown':
                return (currentIdx + 1) % count;
            case 'ArrowLeft':
            case 'ArrowUp':
                return currentIdx === 0 ? count - 1 : currentIdx - 1;
            case 'Home':
                return 0;
            case 'End':
                return count - 1;
            default:
                return null;
        }
    }

    /**
     * Prüft, ob ein Item per Tastatur erreichbar ist: sichtbar, nicht deaktiviert und nicht ausgeblendet.
     * @param el Das zu prüfende Element
     * @returns true, wenn erreichbar
     */
    private isReachable(el: HTMLElement): boolean {
        return $(el).is(':visible')
            && el.getAttribute('aria-disabled') !== 'true'
            && !this.isHiddenInSecondary(el);
    }

    /**
     * Prüft, ob ein Element vom ToolBar-Control als ausgelagert (in das Overflow-Menü verschoben) markiert wurde.
     * @param el Das zu prüfende Element
     * @returns true, wenn das Element ausgelagert ist
     */
    private isHiddenInSecondary(el: HTMLElement): boolean {
        return el.classList.contains('toolbar-overflowed');
    }
}
